import { LoadingContent } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/LoadingContent";
import { MeasureCardHeaderLabel } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/MeasureCardHeaderLabel";
import { MeasureChanges } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/MeasureChanges";
import { usePreviousEventVersion } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/usePreviousEventVersion";
import { MeasureOverviewDataContext } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewDataContext";
import { PlanEventInformation } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/types";
import { FC, useContext } from "react";
import { FailureMessageWithRetryButton } from "shared/components/feedback/FailureMessageWithRetryButton";
import {
  Button,
  DialogCloseTrigger,
  DialogRoot,
  DialogTrigger,
  DropsDialogContent,
  Flex,
  StaticCard,
  Text,
} from "shared/components/Spor";
import { useThemeSemantic } from "shared/theme/themeSemantic";
import { formatMeasureCardDateTimeString } from "shared/utils/dateTimeUtils";
import { Semantics } from "shared/utils/semantics";

type RecentlyUpdatedDialogProps = {
  measure: PlanEventInformation;
  semantics: Semantics;
  acknowledgeLatestVersion: () => void;
};

export const RecentlyUpdatedDialog: FC<RecentlyUpdatedDialogProps> = ({
  measure: { information, internalMessages },
  semantics,
  acknowledgeLatestVersion,
}) => {
  const theme = useThemeSemantic(semantics);

  const { forceRefresh: forceGlobalRecount } = useContext(
    MeasureOverviewDataContext,
  );

  const {
    data: previousMeasure,
    status,
    refetch,
  } = usePreviousEventVersion(information);

  if (status === "success" && !previousMeasure) return null;

  return (
    <DialogRoot placement="center">
      <DialogTrigger asChild>
        <Button variant="tertiary" size="xs" semantic="warning" type="button">
          Se endring
        </Button>
      </DialogTrigger>

      <DropsDialogContent semantics="warning">
        <Flex direction="column" m={4} gap={1}>
          <Text variant="md-lg" fontWeight="bold">
            Endringer på hendelse
          </Text>
          <MeasureCardHeaderLabel information={information} />
          <Text>
            Sist oppdatert:{" "}
            {formatMeasureCardDateTimeString(information.updatedAt)}
          </Text>
          <DialogCloseTrigger />
        </Flex>

        <StaticCard
          backgroundColor={theme.colorWarningFillPulse}
          mx={1}
          p={3}
          display="flex"
          flexFlow="column nowrap"
          gap={3}
        >
          {status === "pending" && (
            <LoadingContent backgroundColor="transparent" />
          )}

          {status === "error" && (
            <FailureMessageWithRetryButton retryFunction={refetch} />
          )}

          {previousMeasure && (
            <MeasureChanges
              currentInformation={information}
              previousInformation={previousMeasure.information}
              currentInternalMessages={internalMessages}
              previousInternalMessages={previousMeasure.internalMessages}
            />
          )}
        </StaticCard>

        <Flex p={4} justifyContent="space-between">
          <DialogTrigger asChild>
            <Button type="button" variant="ghost" size="sm">
              Avbryt
            </Button>
          </DialogTrigger>

          <Button
            semantic="warning"
            type="button"
            variant="primary"
            size="sm"
            onClick={() => {
              acknowledgeLatestVersion();
              forceGlobalRecount();
            }}
            disabled={status !== "success"}
          >
            Bekreft sett
          </Button>
        </Flex>
      </DropsDialogContent>
    </DialogRoot>
  );
};
